import { flexRender, type Row } from "@tanstack/react-table";
import { useHistory } from "react-router-dom";
import {
  type Client,
  type DocumentDetail,
  DocumentSubType,
} from "@/briefpoint-client";
import { TableCell, TableRow } from "@/components/ui/table";
import { useAuth } from "@/hooks/useAuth";
import useConfig, { CLIENT_TASKS_FF } from "@/hooks/useConfig";
import type { ViewCommunicationResponse } from "@/lib/types";
import { sortCommunications } from "@/lib/utils";
import { DocumentListTableRowReview } from "./DocumentListTableRowReview";

type Props = {
  row: Row<DocumentDetail>;
  communications: ViewCommunicationResponse[];
  client?: Client;
  reviewExpanded: boolean;
  setReviewExpanded: (expanded: boolean) => void;
};

export default function DocumentListTableRow(props: Props) {
  const history = useHistory();
  const { user, firm } = useAuth();
  const [, featureFlags] = useConfig();
  const useClientTasks = featureFlags()[CLIENT_TASKS_FF]
    ? featureFlags()[CLIENT_TASKS_FF](user, firm)
    : false;

  const isRFPWithClientTasks =
    useClientTasks &&
    props.row.original.info?.documentSubType ===
      DocumentSubType.RequestsForProduction;

  function getCommunicationForDocument() {
    const communication = props.communications
      ?.filter((c) => c.objectId === props.row.original.id)
      ?.sort(sortCommunications)?.[0];
    return communication;
  }

  const communication = getCommunicationForDocument();

  return (
    <>
      <TableRow
        key={props.row.id}
        data-state={props.row.getIsSelected() && "selected"}
      >
        {props.row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            style={{
              width: (cell.column.columnDef.meta as { width: string })?.width, // taken from column meta definition
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
      {props.reviewExpanded && (
        <TableRow>
          <TableCell
            colSpan={props.row.getVisibleCells().length}
            className="p-0"
          >
            <DocumentListTableRowReview
              communication={communication}
              createReview={() => {
                if (isRFPWithClientTasks) {
                  history.push(`/wizard/${props.row.original.id}`);
                } else {
                  history.push(
                    `/documents/${props.row.original.id}/client-feedback`,
                  );
                }
              }}
              client={props.client}
              closeReview={() => props.setReviewExpanded(false)}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
